// Notification API Service
import { createApiClient } from '../utils/axiosConfig';
import { getApiBaseUrl } from '../utils/apiConfig';

// Use base server URL - normalize to remove trailing /api if present
// Endpoints will include /api prefix
let API_BASE_URL = getApiBaseUrl();
// Remove trailing /api if present to avoid double /api/api/
if (API_BASE_URL.endsWith('/api')) {
  API_BASE_URL = API_BASE_URL.slice(0, -4);
}

// Create axios instance with default config and auth error handling
const apiClient = createApiClient(API_BASE_URL);

// Types
export interface Notification {
  id: string;
  type: 'pr_published' | 'pr_rejected' | 'payment' | 'campaign' | 'system' | 'success' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  metadata?: {
    prId?: string;
    paymentAmount?: number;
    campaignId?: string;
    [key: string]: any;
  };
  createdAt?: Date;
  readAt?: Date;
}

export interface NotificationPreferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
  prUpdates: boolean;
  paymentAlerts: boolean;
  campaignUpdates: boolean;
  systemAlerts: boolean;
}

// API Methods
export const notificationApi = {
  // Get all user notifications
  getUserNotifications: async (page = 1, limit = 50, unreadOnly = false): Promise<{
    success: boolean;
    notifications: Notification[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    try {
      const response = await apiClient.get('/api/notifications/user', {
        params: { page, limit, unreadOnly: unreadOnly ? 'true' : 'false' }
      });
      
      // Transform timestamp strings to Date objects
      if (response.data.notifications) {
        response.data.notifications = response.data.notifications.map((notif: any) => ({
          ...notif,
          timestamp: new Date(notif.timestamp || notif.createdAt),
          createdAt: notif.createdAt ? new Date(notif.createdAt) : undefined,
          readAt: notif.readAt ? new Date(notif.readAt) : undefined,
        }));
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Get notifications error:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch notifications');
    }
  },

  // Mark notification as read
  markAsRead: async (notificationId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.put(`/api/notifications/read/${notificationId}`);
      return response.data;
    } catch (error: any) {
      console.error('Mark as read error:', error);
      throw new Error(error.response?.data?.message || 'Failed to mark notification as read');
    }
  },

  // Mark all notifications as read
  markAllAsRead: async (): Promise<{ success: boolean; message: string; updatedCount: number }> => {
    try {
      const response = await apiClient.put('/api/notifications/read-all');
      return response.data;
    } catch (error: any) {
      console.error('Mark all as read error:', error);
      throw new Error(error.response?.data?.message || 'Failed to mark all notifications as read');
    }
  },

  // Delete notification
  deleteNotification: async (notificationId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.delete(`/api/notifications/${notificationId}`);
      return response.data;
    } catch (error: any) {
      console.error('Delete notification error:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete notification');
    }
  },

  // Create notification (admin/system)
  createNotification: async (notification: {
    type: string;
    title: string;
    message: string;
    actionUrl?: string;
    metadata?: any;
  }): Promise<{ success: boolean; notification: Notification }> => {
    try {
      const response = await apiClient.post('/api/notifications/create', notification);
      return response.data;
    } catch (error: any) {
      console.error('Create notification error:', error);
      throw new Error(error.response?.data?.message || 'Failed to create notification');
    }
  },

  // Get notification preferences
  getPreferences: async (): Promise<{ success: boolean; preferences: NotificationPreferences }> => {
    try {
      const response = await apiClient.get('/api/notifications/preferences');
      return response.data;
    } catch (error: any) {
      console.error('Get preferences error:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch notification preferences');
    }
  },

  // Update notification preferences
  updatePreferences: async (preferences: NotificationPreferences): Promise<{ 
    success: boolean; 
    message: string;
    preferences: NotificationPreferences;
  }> => {
    try {
      const response = await apiClient.put('/api/notifications/preferences', preferences);
      return response.data;
    } catch (error: any) {
      console.error('Update preferences error:', error);
      throw new Error(error.response?.data?.message || 'Failed to update notification preferences');
    }
  },
};

export default notificationApi;



